import { useEffect, useRef } from 'react';
import { createStoreHooks, type FeatureDeps } from '../../../../canvas/core';
import type { PageLayoutData } from '../../../core';
import {
  type CanvasRendererFn,
  createPageCanvasOverlayFeature,
} from '../feature';

type PageCanvasOverlayDeps = FeatureDeps<
  [typeof createPageCanvasOverlayFeature]
>;

const { useStore } = createStoreHooks<PageCanvasOverlayDeps>();

type PageCanvasOverlayProps = {
  pageLayout: PageLayoutData;
};

const EMPTY_RENDERERS: { id: string; render: CanvasRendererFn }[] = [];

export const PageCanvasOverlay = ({ pageLayout }: PageCanvasOverlayProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderers = useStore(
    state =>
      state.pageCanvasOverlay.state.renderers[pageLayout.pageId] ??
      EMPTY_RENDERERS
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const displayWidth = pageLayout.originalWidth;
    const displayHeight = pageLayout.originalHeight;

    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;
    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;

    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, displayWidth, displayHeight);

    renderers.forEach((renderer: { id: string; render: CanvasRendererFn }) => {
      ctx.save();
      renderer.render(ctx, displayWidth, displayHeight);
      ctx.restore();
    });
  }, [renderers, pageLayout.originalWidth, pageLayout.originalHeight]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
      }}
    />
  );
};
